import { Component, inject, Injector, input, OnInit, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { distinctUntilChanged, startWith } from 'rxjs';
import { EBigNumberFormatColor, EFormatConditionOperator, IBigNumberResultFormat } from 'scw-digital-boards-widgets';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';

@Component({
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatDropdown,
    MatIcon,
  ],
  selector: 'threshold-based-coloring-selection',
  styleUrls: ['./threshold-based-coloring-selection.component.scss'],
  templateUrl: './threshold-based-coloring-selection.component.html',
})
export class ThresholdBasedColoringSelectionComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  public readonly formControl = input.required<FormControl<IBigNumberResultFormat[]>>();
  public readonly isFormSubmitted = input.required<boolean>();

  public readonly colorDropdownOptions: IDropdownOption<EBigNumberFormatColor>[] = [
    { id: EBigNumberFormatColor.green, name: this.translate.instant('field.green') },
    { id: EBigNumberFormatColor.red, name: this.translate.instant('field.red') },
  ];

  public readonly colorDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.color'),
  };

  public readonly inputsFormGroup = new FormGroup({
    lowerThreshold: new FormControl<number | null>(null),
    lowerThresholdColor: new FormControl<IDropdownOption<EBigNumberFormatColor>[]>([], { nonNullable: true }),
    upperThreshold: new FormControl<number | null>(null),
    upperThresholdColor: new FormControl<IDropdownOption<EBigNumberFormatColor>[]>([], { nonNullable: true }),
  });

  public ngOnInit(): void {
    const lowerThresholdValue = this.formControl().value.find(
      ({ condition }) => condition.operator === EFormatConditionOperator.lte,
    );
    const upperThresholdValue = this.formControl().value.find(
      ({ condition }) => condition.operator === EFormatConditionOperator.gte,
    );

    this.formControl().addValidators([
      () => {
        return (this.inputsFormGroup.controls.lowerThreshold.value === null ||
          this.inputsFormGroup.controls.lowerThresholdColor.value.length > 0) &&
          (this.inputsFormGroup.controls.upperThreshold.value === null ||
            this.inputsFormGroup.controls.upperThresholdColor.value.length > 0)
          ? {}
          : { required: true };
      },
    ]);
    this.inputsFormGroup.reset({
      lowerThreshold: lowerThresholdValue?.condition.value ?? null,
      lowerThresholdColor: this.colorDropdownOptions.filter(({ id }) => id === lowerThresholdValue?.color),
      upperThreshold: upperThresholdValue?.condition.value ?? null,
      upperThresholdColor: this.colorDropdownOptions.filter(({ id }) => id === upperThresholdValue?.color),
    });

    runInInjectionContext(this.injector, () => {
      this.applyCompleteInputsIntoFormControlSubscription();
      this.makeTheColorRequiredSubscription();
    });
  }

  private makeTheColorRequiredSubscription(): void {
    this.inputsFormGroup.controls.lowerThreshold.valueChanges
      .pipe(
        startWith(this.inputsFormGroup.controls.lowerThreshold.value),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(),
      )
      .subscribe((value) => {
        if (value === null) {
          this.inputsFormGroup.controls.lowerThresholdColor.setValidators([]);
          this.inputsFormGroup.controls.lowerThresholdColor.disable(); // Disable => enable is there for asterisk add/remove on dropdown component
          this.inputsFormGroup.controls.lowerThresholdColor.enable();
          this.inputsFormGroup.controls.lowerThresholdColor.updateValueAndValidity();
        } else {
          this.inputsFormGroup.controls.lowerThresholdColor.setValidators([Validators.required]);
          this.inputsFormGroup.controls.lowerThresholdColor.disable(); // Disable => enable is there for asterisk add/remove on dropdown component
          this.inputsFormGroup.controls.lowerThresholdColor.enable();
          this.inputsFormGroup.controls.lowerThresholdColor.updateValueAndValidity();
        }
      });
  }

  private applyCompleteInputsIntoFormControlSubscription(): void {
    this.inputsFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      const filters: IBigNumberResultFormat[] = [];

      if (
        this.inputsFormGroup.controls.lowerThreshold.value !== null &&
        this.inputsFormGroup.controls.lowerThresholdColor.value.length
      ) {
        filters.push({
          color: this.inputsFormGroup.controls.lowerThresholdColor.value[0].id,
          condition: {
            operator: EFormatConditionOperator.lte,
            value: this.inputsFormGroup.controls.lowerThreshold.value,
          },
        });
      }

      if (
        this.inputsFormGroup.controls.upperThreshold.value !== null &&
        this.inputsFormGroup.controls.upperThresholdColor.value.length
      ) {
        filters.push({
          color: this.inputsFormGroup.controls.upperThresholdColor.value[0].id,
          condition: {
            operator: EFormatConditionOperator.gte,
            value: this.inputsFormGroup.controls.upperThreshold.value,
          },
        });
      }

      this.formControl().setValue(filters);
    });

    this.inputsFormGroup.controls.upperThreshold.valueChanges
      .pipe(
        startWith(this.inputsFormGroup.controls.upperThreshold.value),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(),
      )
      .subscribe((value) => {
        if (value === null) {
          this.inputsFormGroup.controls.upperThresholdColor.setValidators([]);
          this.inputsFormGroup.controls.upperThresholdColor.disable(); // Disable => enable is there for asterisk add/remove on dropdown component
          this.inputsFormGroup.controls.upperThresholdColor.enable();
          this.inputsFormGroup.controls.upperThresholdColor.updateValueAndValidity();
        } else {
          this.inputsFormGroup.controls.upperThresholdColor.setValidators([Validators.required]);
          this.inputsFormGroup.controls.upperThresholdColor.disable(); // Disable => enable is there for asterisk add/remove on dropdown component
          this.inputsFormGroup.controls.upperThresholdColor.enable();
          this.inputsFormGroup.controls.upperThresholdColor.updateValueAndValidity();
        }

        this.formControl().updateValueAndValidity();
      });
  }
}
